import { useCallback, useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { useSelector } from "react-redux";
import { R_CHART_TYPES } from "../../const";
import { fetchModSimulationSummary } from "../../../../Rest_api/DemoRestApi";
import loader from "../../../../components/recharts/loader.gif";
interface SummaryData {
  date: string;
  generator: string;
  time_slot: number;
  total_available: number;
  total_demand: number;
  total_must_run: number;
  dam_price: number;
}
const chartSuffixes = [
  {
    suffix: "total_must_run",
    displayName: "Must Run (MW)",
    stackSuffix: "total_must_run",
    stroke: "rgba(241, 147, 92, 1)",
    chartType: ChartType.Bar,
    unit: "MW",
    yAxisId: "left",
  },
  {
    suffix: "total_available",
    displayName: "Withdrawable Capacity (MW)",
    stackSuffix: "total_available",
    stroke: "rgba(252, 233, 222, 1)",
    legendColor: "rgba(255, 180, 137, 1)",
    chartType: ChartType.Bar,
    unit: "MW",
    yAxisId: "left",
  },
  {
    suffix: "total_demand",
    displayName: "Demand (MW)",
    stackSuffix: "total_demand",
    stroke: "rgba(236, 28, 36, 1)",
    chartType: ChartType.Line,
    unit: "MW",
    yAxisId: "left",
  },

  {
    suffix: "dam_price",
    displayName: "IEX DAM Rate (₹/KWh)",
    stackSuffix: "dam_price",
    stroke: "rgba(52, 101, 109, 1)",
    chartType: ChartType.Line,
    unit: "₹/KWh",
    yAxisId: "right",
  },
];
export default function SimulationSummaryAnylysis({
  selectedBlock,
  refreshKey,
  setError,
  selectedGeneratorName,
}: {
  selectedBlock: any[];
  selectedGeneratorName: any[];
  refreshKey: number;
  setError?: any;
}) {
  const [data, setData] = useState<SummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchModSimulationSummary(
        preSelectedDate,
        modSelectedSession
      );

      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate, setError]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      date: preSelectedDate,
    }));
    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filteredData = filteredData.filter((row) =>
        blocks.includes(Number(row.time_slot))
      );
    }

    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filteredData = filteredData.filter((row) =>
        generator.includes(row.generator)
      );
    }

    const par: ChartDataParams = {
      keys: ["date", "time_slot"],
      tableKeys: ["date", "time_slot", "generator"],
      groupBy: [""],
      volumeKey: ``,
      aggregationKeys: ["total_available", "total_must_run"],
      averageKeys: ["dam_price", "total_demand"],
      averageDuplicationKeys: ["date", "time_slot", "generator"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, preSelectedDate, selectedBlock, selectedGeneratorName]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      legendColor: legendColor,
      type: ["total_demand", "dam_price"].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: "a",
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId
            ) as any
        )
    );
  }

  return (
    <>
      {loading ? (
        <div className="h-full w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={false}
          brushStart={BrushStart.Start}
          isTimeSlot={true}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="MW"
          secondYAxisLabel="₹/kWh"
          nameGap={40}
          xDataKey={"time_slot"}
          top={10} // Top margin for grid
          bottom={30} // Bottom margin for grid
          xTick={(value: any, index: any) => {
            return renderHourTick(value, 10);
          }}
          secondXDataKey={"date"}
          showToolbox={false}
        />
      )}
    </>
  );
}
